import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LearningOverviewCardProps, ContentType, CourseStatus, MeatballMenuItem } from '@atlassian/learning-components';

import { formatDurationFromMinutes } from 'src/utils/resourceCardUtils';
import { getCourseUrl, getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { LearningProgressStatus } from 'src/services/http/progress';
import { NotificationType } from 'src/services/http/notifications';

import { CONTENT_TYPE_VALUES, DEFAULT_PRODUCT, ROUTES } from './constants';
import { FlattenedItem } from './useMyLearningData';

interface CardActionHandlers {
  handleTitleClick: (item: FlattenedItem) => void;
  handleViewUpdates: (item: FlattenedItem, notificationType: NotificationType) => void;
  handleGeneratePdf: (item: FlattenedItem) => Promise<void>;
  handleSave: (
    contentfulId: string,
    contentType: ContentType.LEARNING_PATH | ContentType.COURSE,
    currentIsSaved: boolean,
    learningPathSlug?: string,
  ) => Promise<void>;
}

export const useLearningCardProps = (
  filteredAndSortedItems: FlattenedItem[],
  handlers: CardActionHandlers,
): LearningOverviewCardProps[] => {
  const navigate = useNavigate();

  return useMemo((): LearningOverviewCardProps[] => {
    return filteredAndSortedItems.map((item) => {
      const isLesson = item.contentType === CONTENT_TYPE_VALUES.LESSON;
      const isCourse = item.contentType === CONTENT_TYPE_VALUES.COURSE;
      const isLearningPath = item.contentType === CONTENT_TYPE_VALUES.LEARNING_PATH;

      // Duration is already pre-calculated in useMyLearningData hook
      const duration = item.duration || 0;

      // Build href
      let href = '';

      if (isLesson && item.slug) {
        href = getLessonUrl({
          lessonSlug: item.slug,
          courseSlug: item.courseSlug,
          pathSlug: item.learningPathSlug,
        });
      } else if (isCourse && item.slug) {
        href = withPrefix(getCourseUrl(item.slug, item.learningPathSlug));
      } else if (isLearningPath && item.slug) {
        href = withPrefix(`${ROUTES.PATH_PREFIX}${item.slug}`);
      }

      // Build lessons array
      // For courses: show lessons
      // For learning paths: show courses (not individual lessons)
      interface LessonOrCourseItem {
        title: string;
        slug: string;
        duration: string;
        isCompleted: boolean;
      }

      let lessons: LessonOrCourseItem[] = [];

      if (isCourse && item.lessonsCollection) {
        lessons = item.lessonsCollection.items.map((lesson) => ({
          title: (lesson.title as string) || '',
          slug: (lesson.slug as string) || '',
          duration: formatDurationFromMinutes(lesson.duration || 0),
          isCompleted: (lesson.status as LearningProgressStatus | undefined) === LearningProgressStatus.COMPLETED,
        }));
      } else if (isLearningPath && item.coursesCollection) {
        lessons = item.coursesCollection.items.map((course) => {
          const courseDuration =
            course.lessonsCollection?.items.reduce((sum: number, lesson) => sum + (lesson?.duration || 0), 0) || 0;

          return {
            title: (course.title as string) || '',
            slug: (course.slug as string) || '',
            duration: formatDurationFromMinutes(courseDuration),
            isCompleted: (course.status as LearningProgressStatus | undefined) === LearningProgressStatus.COMPLETED,
          };
        });
      }

      // Build tags
      const tags = [{ label: formatDurationFromMinutes(duration) }, { label: item.product || DEFAULT_PRODUCT }];

      // Build dropdown items
      const dropdownItems: MeatballMenuItem[] = [];

      // 1. "Go to <content type>" item
      if (isLesson || isCourse || isLearningPath) {
        const contentTypeLabel = isLesson ? 'lesson' : isCourse ? 'course' : 'learning path';

        dropdownItems.push({
          label: `Go to ${contentTypeLabel}`,
          onClick: () => handlers.handleTitleClick(item),
        });
      }

      // 2. "Updates" item - only show if there are unseen or historical notifications
      if ((isCourse || isLearningPath) && (item.unseenNotifications?.length || item.historicalNotifications?.length)) {
        if (item.unseenNotifications?.length) {
          dropdownItems.push({
            label: 'Updates',
            onClick: () => handlers.handleViewUpdates(item, NotificationType.UNSEEN),
          });
        } else if (item.historicalNotifications?.length) {
          dropdownItems.push({
            label: 'View updates',
            onClick: () => handlers.handleViewUpdates(item, NotificationType.HISTORICAL),
          });
        }
      }

      return {
        course: {
          title: item.title,
          href,
          subTitle: '',
          duration: '',
          slug: item.slug || '',
          imageUrl: '',
          courseStatus: CourseStatus.DEFAULT,
        },
        lessons,
        onSave:
          isCourse || isLearningPath
            ? () =>
              handlers.handleSave(
                item.contentfulId,
                isCourse ? ContentType.COURSE : ContentType.LEARNING_PATH,
                item.isSaved || false,
                item.learningPathSlug,
              )
            : undefined,
        onTitleClick: () => handlers.handleTitleClick(item),
        onLessonClick: (slug: string) => {
          if (isCourse) {
            navigate(
              getLessonUrl({
                lessonSlug: slug,
                courseSlug: item.slug,
                pathSlug: item.learningPathSlug,
              }),
            );
          } else {
            navigate(getCourseUrl(slug, item.slug!));
          }
        },
        showSaveButton: isCourse || isLearningPath,
        progress: item.subCollectionTotal > 0 ? Math.round((item.progressTotal / item.subCollectionTotal) * 100) : 0,
        type: isLesson ? ContentType.LESSON : isCourse ? ContentType.COURSE : ContentType.LEARNING_PATH,
        isSaved: item.isSaved || false,
        isRetired: item.isRetired || false,
        tags,
        completionBadgeUrl: item.badgeUrl,
        onDownloadPdf:
          (isCourse || isLearningPath) && item.completedDate ? () => handlers.handleGeneratePdf(item) : undefined,
        dropdownItems,
      };
    });
  }, [filteredAndSortedItems, navigate, handlers]);
};
